import {
  Injectable,
  inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Row } from '@vendasta/galaxy/table';
import {
  CrmFieldService,
  StandardExternalIds,
} from '@galaxy/crm/services/field-service';
import { EmailProvider } from './send-email/constants';
import { CrmSendEmailComponent } from './send-email/send-email.component';

export interface EmailComposerData {
  namespace: string;
  userId: string;
  contactIds: string[];
  emails: string[];
  provider: EmailProvider;
}

@Injectable({
  providedIn: 'root',
})
export class EmailWorkflowService {
  private dialog = inject(MatDialog);
  private crmFieldService = inject(CrmFieldService);
  private breakpointObserver = inject(BreakpointObserver);

  /**
   * Open email composer modal with contact IDs and optional email addresses
   * @param namespace The namespace (typically account group ID)
   * @param userId The current user ID
   * @param contactIds Array of contact IDs
   * @param emails Array of email addresses (optional)
   * @param provider Email provider to use ('gmail' or 'outlook')
   */
  openEmailComposer(
    namespace: string,
    userId: string,
    contactIds: string[],
    emails: string[] = [],
    provider: EmailProvider
  ): void {
    const dialogData: EmailComposerData = {
      namespace,
      userId,
      contactIds,
      emails,
      provider,
    };

    // Check if screen is mobile (below 600px / tablet minimum)
    const isMobile = this.breakpointObserver.isMatched('(max-width: 599px)');

    // Always use 100vw/100vh for maxWidth/maxHeight to allow updateSize to work in both directions
    // The actual size is controlled by width/height and the component's updateDialogSize method
    this.dialog.open(CrmSendEmailComponent, {
      data: dialogData,
      width: isMobile ? '100vw' : '1000px',
      height: isMobile ? '100vh' : '95vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      autoFocus: false,
      panelClass: 'email-composer-dialog',
    });
  }

  /**
   * Open email composer modal from contact rows
   * @param namespace The namespace (typically account group ID)
   * @param userId The current user ID
   * @param rows Array of contact rows
   * @param provider Email provider to use ('gmail' or 'outlook')
   */
  openEmailComposerFromRows(
    namespace: string,
    userId: string,
    rows: Row[],
    provider: EmailProvider
  ): void {
    const contactIds = rows.map(row => row.id);
    
    // Extract email addresses from rows
    const emailFieldId = this.crmFieldService.getFieldId(StandardExternalIds.Email);
    const emails = rows.map(row => {
      const emailField = row.data[emailFieldId];
      return emailField?.value || '';
    });
    
    this.openEmailComposer(namespace, userId, contactIds, emails, provider);
  }
}

